import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { t } from "@/i18n/pl";
import { queryClient, queryKeys } from "@/lib/query-client";
import { ApiError } from "@/services/api-client";
import * as adminService from "@/services/admin.service";
import type {
  AdminUserCreateInput,
  AdminUserUpdateInput,
  PickupAreaCreateInput,
  PickupAreaUpdateInput,
} from "@/types/api";

function toastError(error: unknown): void {
  toast.error(error instanceof ApiError ? error.message : t.common.genericError);
}

function invalidateAdmin(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
  void queryClient.invalidateQueries({ queryKey: queryKeys.adminAreas });
  void queryClient.invalidateQueries({ queryKey: queryKeys.pickupAreas });
  // Deactivations ripple into groups (paused crews, freed seats) — the admin
  // "Grupy" list included, which lives under the same key.
  void queryClient.invalidateQueries({ queryKey: queryKeys.groups });
}

export function useAdminUsers() {
  return useQuery({ queryKey: queryKeys.adminUsers, queryFn: adminService.listUsers });
}

export function useAdminGroups() {
  return useQuery({ queryKey: queryKeys.adminGroups, queryFn: adminService.listGroups });
}

export function useAdminStats() {
  return useQuery({ queryKey: queryKeys.adminStats, queryFn: adminService.getStats });
}

export function useAdminAreas(query = "") {
  return useQuery({
    queryKey: [...queryKeys.adminAreas, query],
    queryFn: () => adminService.listAllAreas(query),
    // Keep the previous page on screen while a new search loads.
    placeholderData: (previous) => previous,
  });
}

export function useCreateUser() {
  return useMutation({
    mutationFn: (input: AdminUserCreateInput) => adminService.createUser(input),
    onSuccess: () => {
      invalidateAdmin();
      toast.success(t.admin.userCreated);
    },
    onError: toastError,
  });
}

export function useUpdateUser() {
  return useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: AdminUserUpdateInput }) =>
      adminService.updateUser(userId, input),
    onSuccess: () => {
      invalidateAdmin();
      toast.success(t.admin.userUpdated);
    },
    onError: toastError,
  });
}

export function useAnonymiseUser() {
  return useMutation({
    mutationFn: (userId: string) => adminService.anonymiseUser(userId),
    onSuccess: () => {
      invalidateAdmin();
      toast.success(t.admin.erased);
    },
    onError: toastError,
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) =>
      adminService.resetPassword(userId, newPassword),
    onSuccess: () => toast.success(t.admin.passwordReset),
    onError: toastError,
  });
}

export function useCreateArea() {
  return useMutation({
    mutationFn: (input: PickupAreaCreateInput) => adminService.createArea(input),
    onSuccess: () => {
      invalidateAdmin();
      toast.success(t.admin.areaCreated);
    },
    onError: toastError,
  });
}

export function useUpdateArea() {
  return useMutation({
    mutationFn: ({
      areaId,
      input,
    }: {
      areaId: string;
      input: PickupAreaUpdateInput;
    }) => adminService.updateArea(areaId, input),
    onSuccess: () => {
      invalidateAdmin();
      toast.success(t.admin.areaUpdated);
    },
    onError: toastError,
  });
}
